import React, { useState, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { renderIcon } from '../../../utils/icons';
import api from '../../../utils/api';

export default function WscFilesTab({ items, attachments, attachmentsLoading, canAdmin, isDark, theme, uploadingItemId, filterItemId, onClearFilter, onUpload, onFileSelected, onDelete, fileInputRef, formatFileSize, siteId, onDriveLinked }) {
  const [pendingUploadItemId, setPendingUploadItemId] = useState(null);
  const [driveBrowseItemId, setDriveBrowseItemId] = useState(null);
  const [driveFiles, setDriveFiles] = useState([]);
  const [driveLoading, setDriveLoading] = useState(false);
  const [driveFolderStack, setDriveFolderStack] = useState([]);
  const [driveRootFolderId, setDriveRootFolderId] = useState(null);
  const [driveSearch, setDriveSearch] = useState('');
  const [driveSearching, setDriveSearching] = useState(false);
  const searchTimerRef = useRef(null);
  const dragRef = useRef(null);
  const dragStartRef = useRef(null);
  const [dragPos, setDragPos] = useState(null);

  const onDragStart = useCallback((e) => {
    if (e.target.tagName === 'BUTTON' || e.target.tagName === 'A' || e.target.tagName === 'INPUT') return;
    const rect = dragRef.current.getBoundingClientRect();
    dragStartRef.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    const onMove = (ev) => {
      setDragPos({ x: ev.clientX - dragStartRef.current.x, y: ev.clientY - dragStartRef.current.y });
    };
    const onUp = () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, []);

  React.useEffect(() => {
    if (!document.getElementById('spin-keyframe')) {
      const style = document.createElement('style');
      style.id = 'spin-keyframe';
      style.textContent = '@keyframes spin { to { transform: rotate(360deg) } }';
      document.head.appendChild(style);
    }
  }, []);

  const openDriveBrowser = async (itemId) => {
    setDriveBrowseItemId(itemId);
    setDriveLoading(true);
    setDriveFolderStack([]);
    setDriveSearch('');
    setDriveSearching(false);
    setDragPos(null);
    try {
      const siteData = await api.get('/api/sites/' + siteId);
      const rootFolderId = siteData.site?.drive_folder_id;
      if (!rootFolderId) {
        alert('No Google Drive folder linked to this site');
        setDriveBrowseItemId(null);
        setDriveLoading(false);
        return;
      }
      setDriveRootFolderId(rootFolderId);
      const data = await api.get('/api/drive/browse?folderId=' + rootFolderId);
      setDriveFiles(data.files || []);
    } catch (e) {
      alert('Failed to load Drive: ' + e.message);
    }
    setDriveLoading(false);
  };

  const handleDriveSearch = (query) => {
    setDriveSearch(query);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (!query.trim()) {
      // Clear search — go back to current folder
      setDriveSearching(false);
      const currentFolderId = driveFolderStack.length > 0 ? driveFolderStack[driveFolderStack.length - 1].id : driveRootFolderId;
      if (currentFolderId) {
        setDriveLoading(true);
        api.get('/api/drive/browse?folderId=' + currentFolderId).then(data => {
          setDriveFiles(data.files || []);
          setDriveLoading(false);
        }).catch(() => setDriveLoading(false));
      }
      return;
    }
    searchTimerRef.current = setTimeout(async () => {
      setDriveLoading(true);
      setDriveSearching(true);
      try {
        const data = await api.get('/api/drive/browse?search=' + encodeURIComponent(query.trim()));
        setDriveFiles(data.files || []);
      } catch (e) {
        console.error('Drive search failed:', e);
      }
      setDriveLoading(false);
    }, 400);
  };

  const browseDriveFolder = async (folderId, folderName) => {
    setDriveLoading(true);
    setDriveFolderStack(prev => [...prev, { id: folderId, name: folderName }]);
    try {
      const data = await api.get('/api/drive/browse?folderId=' + folderId);
      setDriveFiles(data.files || []);
    } catch (e) {
      alert('Failed to browse: ' + e.message);
    }
    setDriveLoading(false);
  };

  const driveGoBack = async () => {
    setDriveLoading(true);
    const newStack = [...driveFolderStack];
    newStack.pop();
    setDriveFolderStack(newStack);
    const parentId = newStack.length > 0 ? newStack[newStack.length - 1].id : driveRootFolderId;
    try {
      const data = await api.get('/api/drive/browse?folderId=' + parentId);
      setDriveFiles(data.files || []);
    } catch (e) {
      alert('Failed to navigate: ' + e.message);
    }
    setDriveLoading(false);
  };

  const linkDriveFile = async (file) => {
    try {
      const res = await api.post('/api/items/' + driveBrowseItemId + '/link-drive', {
        file_id: file.id,
        file_name: file.name,
        mime_type: file.mimeType,
        file_size: file.size || 0,
        web_view_url: file.webViewLink
      });
      if (onDriveLinked) onDriveLinked(driveBrowseItemId, res.attachment);
      setDriveBrowseItemId(null);
      setDragPos(null);
    } catch (e) {
      alert('Failed to link: ' + e.message);
    }
  };

  const displayItems = filterItemId ? items.filter(i => i.id === filterItemId) : items;

  if (attachmentsLoading) {
    return (
      <div style={{ fontSize: 13, color: theme.textSecondary, padding: '24px 0', textAlign: 'center' }}>
        Loading files...
      </div>
    );
  }

  const hasAny = displayItems.some(item => (attachments[item.id] || []).length > 0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <input
        ref={fileInputRef}
        type="file"
        style={{ display: 'none' }}
        onChange={(e) => {
          if (e.target.files[0] && pendingUploadItemId) {
            onFileSelected(pendingUploadItemId, e.target.files[0]);
          }
        }}
      />

      {filterItemId && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '4px 8px', borderRadius: 6,
          background: isDark ? 'rgba(59,130,246,0.1)' : 'rgba(59,130,246,0.06)',
          border: '1px solid rgba(59,130,246,0.2)',
          fontSize: 12, color: '#4ADE80',
        }}>
          <span>Filtered to: {(items.find(i => i.id === filterItemId) || {}).doc || 'item'}</span>
          <button
            onClick={onClearFilter}
            style={{
              marginLeft: 'auto', background: 'none', border: 'none',
              color: '#4ADE80', cursor: 'pointer', fontSize: 14, padding: '0 2px',
            }}
          >✕</button>
        </div>
      )}

      {displayItems.map(item => {
        const itemAttachments = attachments[item.id] || [];

        return (
          <div key={item.id} style={{ marginBottom: 4 }}>
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '4px 0', marginBottom: 4,
            }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: theme.text, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {item.doc}
                {itemAttachments.length > 0 && (
                  <span style={{ color: theme.textSecondary, fontWeight: 400, marginLeft: 4 }}>
                    ({itemAttachments.length})
                  </span>
                )}
              </span>
              {canAdmin && (
                <div style={{ display: 'flex', gap: 4 }}>
                  <button
                    onClick={() => {
                      setPendingUploadItemId(item.id);
                      setTimeout(() => {
                        if (fileInputRef.current) {
                          fileInputRef.current.value = '';
                          fileInputRef.current.click();
                        }
                      }, 50);
                    }}
                    disabled={uploadingItemId === item.id}
                    style={{
                      padding: '3px 8px', borderRadius: 5, fontSize: 11, fontWeight: 600,
                      border: '1px solid ' + theme.border, cursor: 'pointer',
                      background: 'rgba(255,255,255,0.05)',
                      color: theme.textSecondary,
                      opacity: uploadingItemId === item.id ? 0.5 : 1,
                    }}
                  >
                    {uploadingItemId === item.id ? 'Uploading...' : 'Upload'}
                  </button>
                  <button
                    onClick={() => openDriveBrowser(item.id)}
                    style={{
                      padding: '3px 8px', borderRadius: 5, fontSize: 11, fontWeight: 600,
                      border: '1px solid ' + theme.border, cursor: 'pointer',
                      background: isDark ? 'rgba(66,133,244,0.1)' : 'rgba(66,133,244,0.06)',
                      color: '#4285f4',
                    }}
                  >
                    📁 Drive
                  </button>
                </div>
              )}
            </div>
            {uploadingItemId === item.id && (
              <div style={{ fontSize: 10, color: '#4ADE80', padding: '4px 0', display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ display: 'inline-block', width: 8, height: 8, border: '2px solid #4ADE80', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                Uploading...
              </div>
            )}

            {itemAttachments.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {itemAttachments.map(att => {
                  const hasBlobUrl = att.file_url && !att.file_url.startsWith('https://drive.google.com');
                  const isDriveFile = att.storage_type === 'google_drive' && !hasBlobUrl;
                  const isImage = hasBlobUrl && att.file_name && /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(att.file_name);
                  const fileUrl = hasBlobUrl ? att.file_url : (att.drive_view_url || att.file_url);
                  const hasAlsoDrive = att.drive_file_id || att.drive_view_url;
                  const uploadDate = att.uploaded_at ? new Date(att.uploaded_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';

                  return (
                    <div
                      key={att.id}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        padding: '6px 8px', borderRadius: 6,
                        background: 'rgba(255,255,255,0.03)',
                        border: '1px solid ' + theme.border,
                      }}
                    >
                      <div style={{ position: 'relative', flexShrink: 0 }}>
                        {isImage ? (
                          <img
                            src={att.file_url}
                            alt={att.file_name}
                            style={{
                              width: 32, height: 32, borderRadius: 4,
                              objectFit: 'cover',
                              background: 'rgba(255,255,255,0.10)',
                            }}
                          />
                        ) : (
                          renderIcon('file', 18)
                        )}
                        {hasAlsoDrive && (
                          <div style={{
                            position: 'absolute', bottom: -2, right: -2,
                            width: 12, height: 12, borderRadius: '50%',
                            background: '#4285f4', display: 'flex',
                            alignItems: 'center', justifyContent: 'center',
                            fontSize: 7, fontWeight: 800, color: '#fff',
                            border: `1.5px solid ${theme.bgCard}`
                          }}>G</div>
                        )}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <a
                          href={fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={isDriveFile ? 'Open in Drive ↗' : att.file_name}
                          style={{
                            fontSize: 12, fontWeight: 500, color: isDriveFile ? '#4285f4' : theme.accent,
                            textDecoration: 'none', display: 'block',
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          }}
                          onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'}
                          onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}
                        >
                          {att.file_name}{isDriveFile ? ' ↗' : ''}
                        </a>
                        <div style={{ fontSize: 10, color: theme.textSecondary }}>
                          {formatFileSize(att.file_size)}
                          {att.uploader_name ? ' · ' + att.uploader_name : ''}
                          {uploadDate ? ' · ' + uploadDate : ''}
                        </div>
                      </div>
                      {canAdmin && (
                        <button
                          onClick={() => onDelete(item.id, att.id)}
                          title="Delete"
                          style={{
                            background: 'none', border: 'none', cursor: 'pointer',
                            color: theme.textSecondary, fontSize: 14, padding: '2px 4px',
                            borderRadius: 4, flexShrink: 0,
                            opacity: 0.6, transition: 'opacity 0.15s',
                          }}
                          onMouseEnter={e => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.color = '#EF4444'; }}
                          onMouseLeave={e => { e.currentTarget.style.opacity = '0.6'; e.currentTarget.style.color = theme.textSecondary; }}
                        >
                          {renderIcon('trash-2', 14)}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ fontSize: 11, color: theme.textSecondary, padding: '2px 0', fontStyle: 'italic' }}>
                No files
              </div>
            )}
          </div>
        );
      })}

      {!hasAny && !filterItemId && (
        <div style={{ fontSize: 13, color: theme.textSecondary, padding: '16px 0', textAlign: 'center' }}>
          No files attached yet
        </div>
      )}

      {/* Drive Browser — portaled to document.body so it's always on top */}
      {driveBrowseItemId && ReactDOM.createPortal((() => {
        const targetItem = items.find(i => i.id === driveBrowseItemId);
        const closePicker = () => { setDriveBrowseItemId(null); setDragPos(null); setDriveSearch(''); setDriveSearching(false); };
        const folders = driveFiles.filter(f => f.mimeType === 'application/vnd.google-apps.folder');
        const files = driveFiles.filter(f => f.mimeType !== 'application/vnd.google-apps.folder');
        return (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', zIndex: 10000,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }} onClick={closePicker}>
          <div ref={dragRef} style={{
            background: theme.bgCard,
            borderRadius: 16, width: 720, maxWidth: '94vw', height: '80vh', maxHeight: 700,
            display: 'flex', flexDirection: 'column',
            boxShadow: '0 24px 80px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.06)',
            ...(dragPos ? { position: 'fixed', left: dragPos.x, top: dragPos.y } : {}),
          }} onClick={e => e.stopPropagation()}>

            {/* Header — drag handle */}
            <div
              onMouseDown={onDragStart}
              style={{
                padding: '20px 24px 16px', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
                cursor: 'grab', userSelect: 'none', borderBottom: '1px solid ' + theme.border,
              }}
            >
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: 10,
                    background: 'linear-gradient(135deg, #4285f4, #34a853)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 18, color: '#fff', fontWeight: 700, flexShrink: 0,
                  }}>G</div>
                  <div>
                    <div style={{ fontSize: 18, fontWeight: 400, fontFamily: "'DM Sans', sans-serif", letterSpacing: '-0.01em', color: theme.text }}>Google Drive</div>
                    {targetItem && (
                      <div style={{ fontSize: 12, color: theme.textSecondary, marginTop: 1 }}>
                        Linking to <span style={{ color: '#4285f4', fontWeight: 600 }}>{targetItem.doc}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <button onClick={closePicker} style={{
                background: 'rgba(255,255,255,0.06)',
                border: 'none', width: 32, height: 32, borderRadius: 8,
                cursor: 'pointer', color: theme.textSecondary, fontSize: 16,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'background 0.15s',
              }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.12)'}
                onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.06)'}
              >✕</button>
            </div>

            {/* Search bar */}
            <div style={{ padding: '12px 24px', borderBottom: '1px solid ' + theme.border }}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 10,
                background: 'rgba(255,255,255,0.05)',
                borderRadius: 10, padding: '0 14px', height: 40,
                border: '1px solid rgba(255,255,255,0.08)',
                transition: 'border-color 0.15s',
              }}>
                <span style={{ color: theme.textSecondary, fontSize: 14, flexShrink: 0 }}>🔍</span>
                <input
                  type="text"
                  placeholder="Search files and folders..."
                  value={driveSearch}
                  onChange={e => handleDriveSearch(e.target.value)}
                  style={{
                    flex: 1, border: 'none', outline: 'none', background: 'transparent',
                    fontSize: 14, color: theme.text,
                  }}
                />
                {driveSearch && (
                  <button onClick={() => handleDriveSearch('')} style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: theme.textSecondary, fontSize: 14, padding: 2,
                  }}>✕</button>
                )}
              </div>
            </div>

            {/* Breadcrumb — hidden during search */}
            {!driveSearching && (
              <div style={{
                padding: '8px 24px', borderBottom: '1px solid ' + theme.border,
                display: 'flex', alignItems: 'center', gap: 6, fontSize: 13,
                color: theme.textSecondary, flexWrap: 'wrap', minHeight: 36,
              }}>
                <span style={{ cursor: 'pointer', color: '#4285f4', fontWeight: driveFolderStack.length === 0 ? 600 : 400 }}
                  onClick={() => { setDriveFolderStack([]); setDriveSearch(''); setDriveSearching(false); openDriveBrowser(driveBrowseItemId); }}>
                  📂 Root
                </span>
                {driveFolderStack.map((f, i) => (
                  <React.Fragment key={i}>
                    <span style={{ color: theme.textSecondary, fontSize: 11 }}>▸</span>
                    <span style={{
                      cursor: i < driveFolderStack.length - 1 ? 'pointer' : 'default',
                      color: i < driveFolderStack.length - 1 ? '#4285f4' : theme.text,
                      fontWeight: i === driveFolderStack.length - 1 ? 600 : 400,
                    }} onClick={() => {
                      if (i < driveFolderStack.length - 1) {
                        const newStack = driveFolderStack.slice(0, i + 1);
                        setDriveFolderStack(newStack);
                        setDriveLoading(true);
                        api.get('/api/drive/browse?folderId=' + f.id).then(data => {
                          setDriveFiles(data.files || []);
                          setDriveLoading(false);
                        });
                      }
                    }}>{f.name}</span>
                  </React.Fragment>
                ))}
              </div>
            )}

            {driveSearching && (
              <div style={{
                padding: '8px 24px', borderBottom: '1px solid ' + theme.border,
                fontSize: 12, color: theme.textSecondary,
              }}>
                Search results for "<strong style={{ color: theme.text }}>{driveSearch}</strong>"
                {!driveLoading && <span> · {driveFiles.length} found</span>}
              </div>
            )}

            {/* File list */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '8px 16px' }}>
              {driveLoading ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 60, gap: 12 }}>
                  <span style={{ display: 'inline-block', width: 24, height: 24, border: '3px solid ' + theme.border, borderTopColor: '#4285f4', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                  <span style={{ fontSize: 13, color: theme.textSecondary }}>Loading...</span>
                </div>
              ) : driveFiles.length === 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 60, gap: 8 }}>
                  <span style={{ fontSize: 32, opacity: 0.4 }}>{driveSearching ? '🔍' : '📂'}</span>
                  <span style={{ fontSize: 14, color: theme.textSecondary }}>{driveSearching ? 'No files found' : 'Empty folder'}</span>
                </div>
              ) : (
                <>
                  {/* Folders first */}
                  {folders.length > 0 && (
                    <div style={{ marginBottom: files.length > 0 ? 8 : 0 }}>
                      <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: theme.textSecondary, padding: '8px 8px 4px', opacity: 0.7 }}>
                        Folders ({folders.length})
                      </div>
                      {folders.map(file => (
                        <div key={file.id} onClick={() => { browseDriveFolder(file.id, file.name); setDriveSearch(''); setDriveSearching(false); }}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 12,
                            padding: '10px 12px', borderRadius: 10, cursor: 'pointer',
                            transition: 'background 0.1s',
                          }}
                          onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                        >
                          <span style={{ fontSize: 22, flexShrink: 0 }}>📁</span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 14, fontWeight: 500, color: theme.text }}>{file.name}</div>
                          </div>
                          <span style={{ color: theme.textSecondary, fontSize: 16 }}>›</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Files */}
                  {files.length > 0 && (
                    <div>
                      {folders.length > 0 && (
                        <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: theme.textSecondary, padding: '8px 8px 4px', opacity: 0.7 }}>
                          Files ({files.length})
                        </div>
                      )}
                      {files.map(file => (
                        <div key={file.id} onClick={() => linkDriveFile(file)}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 12,
                            padding: '10px 12px', borderRadius: 10, cursor: 'pointer',
                            transition: 'background 0.1s',
                          }}
                          onMouseEnter={e => e.currentTarget.style.background = 'rgba(66,133,244,0.08)'}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                        >
                          <span style={{ fontSize: 22, flexShrink: 0 }}>
                            {file.mimeType?.includes('spreadsheet') ? '📊' : file.mimeType?.includes('document') ? '📝' : file.mimeType?.includes('presentation') ? '📽️' : file.mimeType?.includes('pdf') ? '📕' : file.mimeType?.includes('image') ? '🖼️' : '📄'}
                          </span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 14, fontWeight: 500, color: theme.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {file.name}
                            </div>
                            <div style={{ fontSize: 11, color: theme.textSecondary, marginTop: 2 }}>
                              {file.modifiedTime ? new Date(file.modifiedTime).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}
                              {file.size ? ' · ' + (parseInt(file.size) > 1048576 ? (parseInt(file.size)/1048576).toFixed(1) + ' MB' : Math.round(parseInt(file.size)/1024) + ' KB') : ''}
                              {file.lastModifyingUser?.displayName ? ' · ' + file.lastModifyingUser.displayName : ''}
                            </div>
                          </div>
                          <button style={{
                            padding: '5px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600,
                            background: '#4285f4', color: '#fff', border: 'none', cursor: 'pointer',
                            transition: 'background 0.15s', flexShrink: 0,
                          }}
                            onMouseEnter={e => e.currentTarget.style.background = '#3367d6'}
                            onMouseLeave={e => e.currentTarget.style.background = '#4285f4'}
                            onClick={e => { e.stopPropagation(); linkDriveFile(file); }}
                          >Link</button>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Footer */}
            <div style={{
              padding: '12px 24px', borderTop: '1px solid ' + theme.border,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              fontSize: 11, color: theme.textSecondary,
            }}>
              <span>Click a file to link it · Drag header to move</span>
              <button onClick={closePicker} style={{
                padding: '6px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                background: 'rgba(255,255,255,0.06)',
                border: '1px solid ' + theme.border, cursor: 'pointer', color: theme.text,
              }}>Cancel</button>
            </div>
          </div>
        </div>
        );
      })(), document.body)}
    </div>
  );
}
